/**
 * Variant URL resolver — picks the right-size WebP variant for a stored
 * image URL, and ALWAYS falls back to the original when it can't.
 *
 * The backend resize pipeline generates variants into `_resized/` subfolders;
 * `@lemonadetraining/contracts` is the frozen cross-platform contract for where
 * those variants live and which serving branch a path uses:
 *
 *   public  → the variant URL is a pure string (no network round-trip):
 *             https://storage.googleapis.com/{bucket}/{variantPath}
 *   tokened → the variant URL comes from getDownloadURL (capability token;
 *             profile pictures and user landing banners never carry a
 *             permanent public object ACL)
 *
 * Both branches append `?v={originalToken}` — the original's download token
 * rotates on every re-upload, so it both busts long-lived edge/browser caches
 * and keys the in-memory result cache (a re-upload is a new cache entry;
 * a stale avatar can never be served from cache after the token rotated).
 *
 * Fallback rules (the resolver must never break an image):
 * - Serving flag off/unset/unreadable → original, untouched.
 * - Not a Firebase/GCS storage URL (blob:, data:, external) → original.
 * - URL naming any bucket other than the app's own → original.
 * - Path outside the image contract → original.
 * - Tokened lookup rejects with object-not-found (variant not generated —
 *   e.g. pre-backfill) → original, and THAT miss is cached for the session
 *   so cold lists don't re-probe storage once per render. Transient errors
 *   (network, auth hiccups) also fall back but are NOT cached — the next
 *   render retries.
 * Public-branch misses are detected by the CONSUMING HOOK's preload (the
 *   resolver itself has no round-trip on this branch): a variant URL whose
 *   bytes never load — missing variant, SVG original with no variant — is
 *   simply never swapped in, and the hook records the fallback.
 */

import { ref, getDownloadURL } from "firebase/storage";
import {
  buildVariantPath,
  buildPublicVariantUrl,
  parseStorageUrl as parseContractStorageUrl,
  servingBranchForPath,
  IMAGE_SERVING_BRANCH,
  VARIANT_CACHE_BUST_PARAM,
} from "@lemonadetraining/contracts";
import type { ImageVariantSize, ParsedStorageUrl } from "@lemonadetraining/contracts";
import type { ImageSurface } from "../constants/imageAnalyticsEvents";
import { firebaseStorage } from "../store/firebaseApp";
import { recordVariantOutcome } from "./imageAnalytics";

/* True only for emulator/dev builds — the ONLY context where localhost
 * storage hosts are legitimate. A stored localhost URL in a production
 * build must stay passthrough: trusted code never launders an
 * attacker-influenceable origin, even a nil-harm one. */
function isEmulatorBuild(): boolean {
  try {
    return (
      process.env.REACT_APP_USE_EMULATOR === "true" ||
      (typeof window !== "undefined" && window.location.hostname === "localhost")
    );
  } catch {
    return false;
  }
}

/**
 * Web-side parser: the cross-platform contract parser bound to THIS build's
 * emulator/dev detection. The contract cannot read `process.env`/`window`, so
 * localhost/emulator storage hosts are accepted only when this build says so —
 * a stored localhost URL in a production build stays passthrough. Anything not
 * a recognized storage URL (blob: previews, data: URIs, external images)
 * returns null and passes through the resolver untouched.
 */
export function parseStorageUrl(url: string): ParsedStorageUrl | null {
  return parseContractStorageUrl(url, { allowEmulatorHosts: isEmulatorBuild() });
}

/**
 * Build-time serving flag. Default CLOSED: variants serve only when the
 * build was made with REACT_APP_SERVE_IMAGE_VARIANTS=true; any problem
 * reading the flag counts as off.
 */
function isServingEnabled(): boolean {
  try {
    return process.env.REACT_APP_SERVE_IMAGE_VARIANTS === "true";
  } catch {
    return false;
  }
}

/* Session cache: `${originalUrl}|${size}` → entry. Both stored URL shapes
 * embed the original's rotating token, so a re-upload is a new key — the
 * cache is self-invalidating. Confirmed misses (object-not-found) are cached
 * too, so a cold pre-backfill list never re-probes storage per render.
 * Bounded by FIFO eviction (Map preserves insertion order) — the cache is a
 * per-session accelerator, not a datastore.
 *
 * `outcome` is the settled tokened-branch result, kept so CACHE HITS can
 * still record the calling surface's hit/fallback counter — the variant
 * coverage metric must count every surface that served the image, not just
 * whichever surface resolved it first. Public-branch entries carry no
 * outcome (the consumer's onError owns that metric). */
const MAX_CACHE_ENTRIES = 1000;

interface VariantCacheEntry {
  promise: Promise<string>;
  outcome?: "hit" | "fallback";
}

const variantUrlCache = new Map<string, VariantCacheEntry>();

/** Empties the resolver cache (tests; or after sign-out if ever needed). */
export function clearImageVariantCache(): void {
  variantUrlCache.clear();
}

/**
 * Resolves the variant URL for a stored image URL at the given size,
 * falling back to the original URL in every non-happy path. `surface` labels
 * the hit/fallback counters (session-aggregated variant-coverage metric) —
 * always label with the real consuming surface so upload and serving
 * counters can be joined per surface.
 */
export function resolveImageVariant(
  originalUrl: string,
  size: ImageVariantSize,
  surface: ImageSurface
): Promise<string> {
  if (!isServingEnabled() || typeof originalUrl !== "string" || originalUrl === "") {
    return Promise.resolve(originalUrl);
  }

  const cacheKey = `${originalUrl}|${size}`;
  const cached = variantUrlCache.get(cacheKey);
  if (cached) {
    // A cache hit still SERVED this surface's image — record its outcome so
    // per-surface coverage counters stay truthful.
    void cached.promise.then(() => {
      if (cached.outcome) recordVariantOutcome(surface, cached.outcome);
    });
    return cached.promise;
  }

  const parsed = parseStorageUrl(originalUrl);
  if (!parsed) return Promise.resolve(originalUrl);

  // Only resolve URLs pointing at the app's own bucket — a stored URL naming
  // any other bucket (or a build with no bucket configured) passes through
  // untouched: the resolver must never be a vehicle for addressing foreign
  // buckets, so this check fails CLOSED.
  const appBucket = firebaseStorage.app.options.storageBucket;
  if (!appBucket || parsed.bucket !== appBucket) {
    return Promise.resolve(originalUrl);
  }

  const branch = servingBranchForPath(parsed.objectPath);
  if (!branch) return Promise.resolve(originalUrl);

  const variantPath = buildVariantPath(parsed.objectPath, size);
  const entry: VariantCacheEntry = { promise: Promise.resolve(originalUrl) };
  if (branch === IMAGE_SERVING_BRANCH.PUBLIC) {
    // Pure string, no round-trip. Emulator originals keep their variant URLs
    // on the emulator origin (an absolute storage.googleapis.com URL would
    // bypass the emulator entirely); production originals get the GCS form.
    entry.promise = Promise.resolve(
      buildPublicVariantUrl({
        bucket: parsed.bucket,
        variantPath,
        token: parsed.token,
        emulatorOrigin: parsed.emulatorOrigin,
      })
    );
  } else {
    entry.promise = getDownloadURL(ref(firebaseStorage, variantPath))
      .then((variantUrl) => {
        entry.outcome = "hit";
        recordVariantOutcome(surface, "hit");
        return parsed.token
          ? `${variantUrl}&${VARIANT_CACHE_BUST_PARAM}=${encodeURIComponent(parsed.token)}`
          : variantUrl;
      })
      .catch((error: { code?: string }) => {
        recordVariantOutcome(surface, "fallback");
        if (error?.code === "storage/object-not-found") {
          // Confirmed missing (pre-backfill): cache the fallback for the
          // session so lists never re-probe per render.
          entry.outcome = "fallback";
        } else {
          // Transient (network/auth): fall back now, retry on a later render.
          variantUrlCache.delete(cacheKey);
        }
        return originalUrl;
      });
  }

  if (variantUrlCache.size >= MAX_CACHE_ENTRIES) {
    variantUrlCache.delete(variantUrlCache.keys().next().value as string);
  }
  variantUrlCache.set(cacheKey, entry);
  return entry.promise;
}
